import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Users } from 'lucide-react';
import InfoPopoverButton from '../../components/InfoPopoverButton';
import { toast } from 'sonner';
import axios from 'axios';
import StaffBottomNav from '../../components/StaffBottomNav';
import VenueEmptyState from '../../components/venue/VenueEmptyState';
import StaffVenueHeader from '../../components/staff/StaffVenueHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessageBubbleIcon = () => (
  <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.09964 12.1082C8.35641 12.1082 7.75391 11.323 7.75391 10.3543C7.75391 9.38561 8.35641 8.60034 9.09964 8.60034C9.84287 8.60034 10.4454 9.38561 10.4454 10.3543C10.4454 11.323 9.84287 12.1082 9.09964 12.1082ZM13.5854 12.1082C12.8422 12.1082 12.2397 11.323 12.2397 10.3543C12.2397 9.38561 12.8422 8.60034 13.5854 8.60034C14.3287 8.60034 14.9312 9.38561 14.9312 10.3543C14.9312 11.323 14.3287 12.1082 13.5854 12.1082ZM18.0712 12.1082C17.328 12.1082 16.7255 11.323 16.7255 10.3543C16.7255 9.38561 17.328 8.60034 18.0712 8.60034C18.8144 8.60034 19.417 9.38561 19.417 10.3543C19.417 11.323 18.8144 12.1082 18.0712 12.1082Z" fill="currentColor"/>
    <path d="M26.0005 11.7233C26.0005 17.6456 17.9017 18.682 11.5005 20.051C4.50049 27 6.4084 28 6.4084 20.051C-6.78226 9.6699 7.48209 1 14.0707 1C20.6593 1 26.0005 5.80098 26.0005 11.7233Z" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

function StaffAvatar({ name, isOwner, isAdmin }) {
  const initials = name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?';
  const colors = ['#E8D5B7', '#D5C4A1', '#F0D9B5', '#C7B299', '#DEC8A8', '#E6CCB2'];
  const colorIdx = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: colors[colorIdx], display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--surface)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: '#5F4D34' }}>{initials}</span>
      </div>
      {isOwner ? <div style={{ position: 'absolute', bottom: -2, left: -4 }}><img src="/allure-logo.png" alt="Owner" style={{ width: 20, height: 18, objectFit: 'contain' }} /></div> : null}
      {isAdmin && !isOwner ? <div style={{ position: 'absolute', bottom: -4, left: -4 }}><Star size={16} color="#FFD700" fill="#FFD700" /></div> : null}
    </div>
  );
}

export default function StaffMembersPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);

  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; }
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    const activeVenueAppId = localStorage.getItem('activeVenueAppId');
    const headers = { Authorization: `Bearer ${token}` };
    if (activeVenueAppId) headers['X-Active-Venue-App-Id'] = activeVenueAppId;
    return headers;
  };

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/venue/staff`, { headers: getHeaders() });
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch { setMembers([]); }
  };

  const visibleMembers = useMemo(
    () => members.filter(
      (m) => m.user_id !== currentUserId
        && (!m.invite_status || m.invite_status === 'accepted'),
    ),
    [members, currentUserId],
  );

  const handleMessage = async (member) => {
    if (!member.user_id) {
      toast.info(`${member.name || 'This member'} hasn't joined yet`);
      return;
    }
    try {
      const res = await axios.post(
        `${API}/messages/open-conversation`,
        { recipient_id: member.user_id },
        { headers: getHeaders() },
      );
      if (res.data?.id) navigate(`/staff-app/chat/${res.data.id}`);
      else toast.error('Could not start conversation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open chat');
    }
  };

  return (
    <div className="app-shell premium-page" data-testid="staff-members-page" style={{ minHeight: '100dvh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120 }}>
      <StaffVenueHeader roleLabel="Venue staff" testIdPrefix="staff-members" />

      <div style={{ width: '100%', maxWidth: 360, padding: '28px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <InfoPopoverButton
          testId="staff-members-page-info"
          size="sm"
          align="start"
          title="Team members"
          tip="Everyone working at this venue. Admins (gold star) can manage the team. You can message any teammate by tapping their card."
        />
        <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Members</h2>
      </div>

      <div className="premium-card" style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 12, borderRadius: 22, padding: '12px 10px 16px', maxHeight: 460, overflowY: 'auto' }}>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', textAlign: 'right', marginBottom: 8, paddingRight: 4 }}>
          {visibleMembers.length} Member{visibleMembers.length === 1 ? '' : 's'}
        </p>

        {visibleMembers.length === 0 ? (
          <VenueEmptyState
            icon={Users}
            title="You're flying solo here"
            description="Once your owner or admin adds teammates, you'll see them right here."
            testId="staff-members-empty-state"
            compact
          />
        ) : null}

        {visibleMembers.map((member) => (
          <div key={member.id} data-testid={`staff-member-${member.id}`}
            style={{ backgroundColor: 'var(--surface)', borderRadius: 18, border: '1px solid var(--card-border)', padding: '10px 12px', marginBottom: 8, boxShadow: 'var(--premium-shadow-nested)', display: 'flex', alignItems: 'center' }}>
            <StaffAvatar name={member.name} isOwner={member.is_owner} isAdmin={member.role === 'admin'} />
            <span style={{ flex: 1, marginLeft: 12, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{member.name}</span>
            <button onClick={() => handleMessage(member)} data-testid={`staff-msg-${member.id}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, color: 'var(--text-2)' }}>
              <MessageBubbleIcon />
            </button>
          </div>
        ))}
      </div>

      <StaffBottomNav />
    </div>
  );
}
